import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { estoqueApi } from '../../services/api';
import type { MaterialFormData, CategoriaEstoque, UnidadeMedida } from '../../types';

interface MaterialFormProps {
  mode: 'create' | 'edit';
}

const MaterialForm: React.FC<MaterialFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<MaterialFormData>({
    codigo: '',
    nome: '',
    descricao: '',
    categoria: 'OUTRO',
    unidade: 'UN',
    fornecedor_nome: '',
    fornecedor_cnpj: '',
    estoque_minimo: 0,
    estoque_maximo: 0,
    localizacao: '',
    ncm: '',
    ean: '',
    ativo: true,
  });

  useEffect(() => {
    if (mode === 'edit' && id) {
      loadMaterial();
    }
  }, [mode, id]);

  const loadMaterial = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const material = await estoqueApi.getMaterial(Number(id));
      setFormData({
        codigo: material.codigo,
        nome: material.nome,
        descricao: material.descricao || '',
        categoria: material.categoria,
        unidade: material.unidade,
        fornecedor_nome: material.fornecedor_nome || '',
        fornecedor_cnpj: material.fornecedor_cnpj || '',
        estoque_minimo: material.estoque_minimo,
        estoque_maximo: material.estoque_maximo,
        localizacao: material.localizacao || '',
        ncm: material.ncm || '',
        ean: material.ean || '',
        ativo: material.ativo,
      });
    } catch (err) {
      setError('Erro ao carregar material');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.codigo || !formData.nome || !formData.categoria) {
      setError('Código, nome e categoria são obrigatórios');
      return;
    }

    if (formData.estoque_minimo < 0 || formData.estoque_maximo < 0) {
      setError('Estoque mínimo e máximo não podem ser negativos');
      return;
    }

    if (formData.estoque_maximo > 0 && formData.estoque_minimo > formData.estoque_maximo) {
      setError('Estoque mínimo não pode ser maior que o máximo');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (mode === 'create') {
        await estoqueApi.createMaterial(formData);
      } else if (id) {
        await estoqueApi.updateMaterial(Number(id), formData);
      }

      navigate('/estoque/materiais');
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail ||
                      Object.values(err.response?.data || {}).flat().join(', ') ||
                      'Erro ao salvar material';
      setError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const categorias: { value: CategoriaEstoque; label: string }[] = [
    { value: 'QUIMICO', label: 'Produto Químico' },
    { value: 'PECA', label: 'Peça de Veículo' },
    { value: 'CONSUMIVEL', label: 'Consumível' },
    { value: 'OFICINA', label: 'Material de Oficina' },
    { value: 'OUTRO', label: 'Outro' },
  ];

  const unidades: { value: UnidadeMedida; label: string }[] = [
    { value: 'UN', label: 'Unidade' },
    { value: 'L', label: 'Litro' },
    { value: 'ML', label: 'Mililitro' },
    { value: 'KG', label: 'Quilograma' },
    { value: 'G', label: 'Grama' },
    { value: 'M', label: 'Metro' },
    { value: 'M2', label: 'Metro Quadrado' },
    { value: 'M3', label: 'Metro Cúbico' },
    { value: 'CX', label: 'Caixa' },
    { value: 'PC', label: 'Peça' },
  ];

  if (loading && mode === 'edit') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-gray-900">
            {mode === 'create' ? 'Novo Material' : 'Editar Material'}
          </h1>
          <p className="text-xs text-gray-600">
            {mode === 'create'
              ? 'Cadastre um novo material no estoque'
              : 'Edite as informações do material'
            }
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg p-4 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {/* Informações Básicas */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Informações Básicas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Código *
              </label>
              <input
                type="text"
                name="codigo"
                value={formData.codigo}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: MAT-001"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nome *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nome do material"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <textarea
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descrição detalhada do material"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <select
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categorias.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Unidade de Medida *
              </label>
              <select
                name="unidade"
                value={formData.unidade}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {unidades.map(un => (
                  <option key={un.value} value={un.value}>
                    {un.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Fornecedor */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Fornecedor</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nome do Fornecedor
              </label>
              <input
                type="text"
                name="fornecedor_nome"
                value={formData.fornecedor_nome}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Razão social"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                CNPJ do Fornecedor
              </label>
              <input
                type="text"
                name="fornecedor_cnpj"
                value={formData.fornecedor_cnpj}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="00.000.000/0000-00"
              />
            </div>
          </div>
        </div>

        {/* Estoque */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Controle de Estoque</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Estoque Mínimo *
              </label>
              <input
                type="number"
                name="estoque_minimo"
                value={formData.estoque_minimo}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Estoque Máximo *
              </label>
              <input
                type="number"
                name="estoque_maximo"
                value={formData.estoque_maximo}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Localização
              </label>
              <input
                type="text"
                name="localizacao"
                value={formData.localizacao}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Prateleira A-12"
              />
            </div>
          </div>
        </div>

        {/* Códigos */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Códigos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                NCM
              </label>
              <input
                type="text"
                name="ncm"
                value={formData.ncm}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nomenclatura Comum do Mercosul"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Código de Barras (EAN)
              </label>
              <input
                type="text"
                name="ean"
                value={formData.ean}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Código de barras"
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="ativo"
              checked={formData.ativo}
              onChange={handleChange}
              className="mr-2"
            />
            <span className="text-xs font-medium text-gray-700">Material ativo</span>
          </label>
        </div>

        {/* Botões */}
        <div className="flex justify-end gap-3 pt-3 border-t">
          <button
            type="button"
            onClick={() => navigate('/estoque/materiais')}
            className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Salvando...' : mode === 'create' ? 'Criar Material' : 'Salvar Alterações'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MaterialForm;
